import React, { useState } from 'react';
import { alpha, Chip, Grid, IconButton, Paper, Typography } from '@mui/material';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import { useTranslation } from 'react-i18next';
import { CatalogItemOut, Currency } from '../../../../../api';
import { CatalogItemPhoto, CatalogPhotoLightbox } from '../../../../../catalog/CatalogItemPhoto';
import { localizedLabel } from '../../../../../utils/maskUtils';
import { priceFormatterFromCurrency } from '../../../../../utils/utils';
import { BODY_WEIGHT, MUTED_TEXT } from '../../../../agendaView/agendaStyles';

type CatalogItemPickerCardProps = {
  item: CatalogItemOut;
  categoryLabel?: string;
  isSelected: boolean;
  quantity?: number;
  currency: Currency;
  language: 'he' | 'en';
  onToggle: () => void;
};

export const CatalogItemPickerCard = ({
  item,
  categoryLabel,
  isSelected,
  quantity,
  currency,
  language,
  onToggle,
}: CatalogItemPickerCardProps) => {
  const { t } = useTranslation();
  const [isEnlarged, setIsEnlarged] = useState(false);
  const name = localizedLabel(item.name, language);

  return (
    <Paper
      variant="outlined"
      role="button"
      tabIndex={0}
      aria-pressed={isSelected}
      onClick={onToggle}
      onKeyDown={(event: React.KeyboardEvent) => {
        if (event.target !== event.currentTarget) {
          return;
        }
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onToggle();
        }
      }}
      sx={{
        borderRadius: '8px',
        border: '2px solid',
        borderColor: isSelected ? 'primary.main' : 'divider',
        backgroundColor: isSelected ? 'action.selected' : undefined,
        cursor: 'pointer',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        '&:hover': { borderColor: 'primary.light' },
      }}>
      <Grid sx={{ position: 'relative', flexShrink: 0 }}>
        <CatalogItemPhoto photoUrl={item.photo_url} alt={name} size="fill" lazy enlargeOnClick={false} />
        {item.photo_url && (
          <IconButton
            size="small"
            aria-label={t('catalog.enlargePhoto')}
            onClick={(event) => {
              event.stopPropagation();
              setIsEnlarged(true);
            }}
            sx={{
              position: 'absolute',
              top: 4,
              insetInlineStart: 4,
              backgroundColor: (theme) => alpha(theme.palette.background.paper, 0.9),
              '&:hover': { backgroundColor: 'background.paper' },
            }}>
            <ZoomInIcon fontSize="small" />
          </IconButton>
        )}
        {isSelected && (
          <Chip
            size="small"
            color="primary"
            label={`×${quantity ?? 1}`}
            sx={{ position: 'absolute', top: 4, insetInlineEnd: 4 }}
          />
        )}
      </Grid>
      <Grid container direction="column" sx={{ padding: '12px', gap: 0.5 }}>
        <Typography
          sx={{
            fontSize: '18px',
            fontWeight: isSelected ? 700 : 600,
            lineHeight: 1.3,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}>
          {name}
        </Typography>
        <Typography noWrap sx={{ fontSize: '16px', fontWeight: BODY_WEIGHT, color: MUTED_TEXT }}>
          {categoryLabel}
        </Typography>
        <Typography dir="ltr" sx={{ fontSize: '17px', fontWeight: isSelected ? 700 : BODY_WEIGHT }}>
          {priceFormatterFromCurrency(item.price, currency, undefined, false, false)}
        </Typography>
      </Grid>
      {item.photo_url && (
        <CatalogPhotoLightbox
          photoUrl={item.photo_url}
          alt={name}
          open={isEnlarged}
          onClose={() => setIsEnlarged(false)}
        />
      )}
    </Paper>
  );
};
